// The Build step, read-only for now (the editor is step b): each product's BOM grouped by module (packaging in a
// Packaging group of its own), with its rules in plain words ("Wireless only", "by Color: 5 parts") and sub-assemblies
// opening in place, or the variant grid (variants as columns, each cell the part that variant uses; a missing part
// stands out). "Edit BOM" opens the existing editor. Everything is from the BOM index (shared/boms.ts), so it draws at once.
import { ChevronDown, ChevronRight, Grid3x3, ListTree, Package, Pencil } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  type BomIndex,
  type BomItem,
  type BomLineRow,
  type BomRow,
  bomOf,
  builtInside,
  byCm,
  familyKey,
  isPackagingRow,
  lineCell,
  linePartIds,
  ownerLabel,
  revisionState,
  SUPPLY_LABELS,
  variantOptions,
} from "../../../../shared/boms.ts";
import {
  type BomSection,
  columnLabel,
  lineRules,
  NO_SECTION,
  optionPicks,
  type ProjectIndex,
  packagingOf,
  type ScopeProduct,
  sectionsOf,
  variantColumns,
} from "../../../../shared/projects.ts";
import { bomLink, STATE_TONE } from "../../lib/boms.ts";
import { qty } from "../../lib/format.ts";
import { Link, setSearch, useSearch } from "../../lib/router.tsx";
import { Badge, cx } from "../../ui/controls.tsx";
import { Card, StepGaps, type TabProps } from "./Workspace.tsx";

type View = "tree" | "grid";

// Packaging --- a packaging line (isPackagingRow: its part is a packaging part, or the line is counted as packaging)
// is in the Packaging group; its badge says what the packaging part is ("Printed box", "Bag with a label").
type PackBadge = { text: string; title: string; warn?: boolean };
type PackOf = (line: BomLineRow) => PackBadge | null;

function packBadge(px: ProjectIndex, line: BomLineRow): PackBadge | null {
  if (!isPackagingRow(px.ix, line)) return null;
  for (const id of linePartIds(px.ix, line)) {
    const item = px.itemById.get(id);
    const pack = item ? packagingOf(item) : null;
    if (pack?.type) return { text: pack.title, title: `Packaging part. ${pack.says}` };
    if (pack) return { text: "Packaging · Category not set", title: pack.says, warn: true };
  }
  return { text: "Counted as packaging", title: "Not a packaging part, but counted as packaging on this line" };
}

/** A group's header: Packaging with its icon; lines in no module are "Not in a module" beside modules. */
function sectionLabel(s: BomSection, sections: BomSection[]): ReactNode {
  if (s.packaging)
    return (
      <span className="flex items-center gap-1">
        <Package className="size-3" />
        {s.name}
      </span>
    );
  if (s.name !== NO_SECTION) return s.name;
  return sections.some((x) => x.name !== NO_SECTION && !x.packaging) ? "Not in a module" : sections.length > 1 ? "Parts" : "Lines";
}

export function BuildTab(props: TabProps) {
  const { project, scope, px, r, here } = props;
  const ix = px.ix;
  const params = useSearch();
  const view: View = params.get("view") === "grid" ? "grid" : "tree";
  const vendorName = (id: string) => {
    const v = px.vendorById.get(id);
    return v ? v.short_name || v.name : "a vendor";
  };
  const packOf: PackOf = (line) => packBadge(px, line);
  const short = (sku: string) => (project.code && sku.toUpperCase().startsWith(`${project.code.toUpperCase()}-`) ? sku.slice(project.code.length + 1) : sku);

  // Builds the products don't reach, shown after them: product BOMs that name the project while their product is linked
  // to none (link it on Products), then sub-assemblies nothing uses (kits, the dongle).
  const others = useMemo(() => {
    const reached = new Set<string>();
    const walk = (bom: BomRow) => {
      for (const line of ix.linesByBom.get(bom.id) ?? []) {
        const ids = [line.component_item_id, ...(ix.mapsByLine.get(line.id)?.values() ?? [])];
        for (const id of ids) {
          const item = id ? ix.itemById.get(id) : undefined;
          const sub = item?.source === "local" ? bomOf(ix, item) : undefined;
          if (sub && !reached.has(familyKey(sub))) {
            reached.add(familyKey(sub));
            walk(sub);
          }
        }
      }
    };
    const unlinked = scope.families.filter((f) => f.via === "bom");
    for (const p of scope.products) if (p.bom) walk(p.bom);
    for (const f of unlinked) walk(f.bom);
    return [...unlinked, ...scope.families.filter((f) => f.kind === "subassembly" && !reached.has(f.key))];
  }, [ix, scope]);

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-start gap-3">
        <StepGaps r={r} steps={["build", "subassemblies"]} project={project} px={px} here={here} />
        <div className="ml-auto flex shrink-0 rounded-md border border-line bg-surface p-0.5">
          {(
            [
              ["tree", "Modules", ListTree],
              ["grid", "Variant grid", Grid3x3],
            ] as const
          ).map(([v, label, Icon]) => (
            <button
              key={v}
              type="button"
              onClick={() => setSearch({ view: v === "tree" ? null : v })}
              className={cx(
                "flex h-7 items-center gap-1.5 rounded px-2.5 text-[12px]",
                view === v ? "bg-selected font-medium" : "text-muted hover:bg-hover hover:text-text",
              )}
            >
              <Icon className="size-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>
      {scope.products.map((p) => (
        <ProductBuild key={p.link.id} p={p} ix={ix} view={view} short={short} vendorName={vendorName} packOf={packOf} />
      ))}
      {!scope.products.length && (
        <div className="rounded-lg border border-dashed border-line-strong bg-surface p-6 text-center text-muted">Link a product first (Products).</div>
      )}
      {others.length > 0 && <h2 className="mt-2 px-1 text-[12px] font-medium tracking-wide text-faint uppercase">Other builds in this project</h2>}
      {others.map((f) => (
        <SubBuild key={f.key} bom={f.bom} ix={ix} short={short} vendorName={vendorName} packOf={packOf} unlinked={f.via === "bom"} />
      ))}
    </div>
  );
}

function RevisionChips({ ix, revisions, value, onChange }: { ix: BomIndex; revisions: BomRow[]; value: string; onChange: (id: string) => void }) {
  const shown = revisions.filter((b) => b.status !== "retired");
  if (shown.length < 2) return null;
  return (
    <span className="flex gap-1">
      {shown.map((b) => {
        const state = revisionState(ix, b);
        return (
          <button
            key={b.id}
            type="button"
            onClick={() => onChange(b.id)}
            className={cx("rounded px-1.5 py-0.5 text-[11px]", b.id === value ? "bg-selected font-medium" : "text-muted hover:bg-hover")}
          >
            Rev {b.revision} {state}
          </button>
        );
      })}
    </span>
  );
}

function ProductBuild({
  p,
  ix,
  view,
  short,
  vendorName,
  packOf,
}: {
  p: ScopeProduct;
  ix: BomIndex;
  view: View;
  short: (s: string) => string;
  vendorName: (id: string) => string;
  packOf: PackOf;
}) {
  const [revId, setRevId] = useState<string | null>(null);
  const bom = (revId ? ix.bomById.get(revId) : undefined) ?? p.bom;
  if (!bom)
    return (
      <Card title={p.title} note="No BOM yet">
        <div className="px-4 py-3 text-muted">Start its BOM from the Products tab.</div>
      </Card>
    );
  const state = revisionState(ix, bom);
  const options = p.options.map((o) => `${o.name} (${o.values.length > 3 ? o.values.length : o.values.join(", ")})`).join(" × ");
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {p.title}
          <span className="font-normal text-muted">Rev {bom.revision}</span>
          <Badge tone={STATE_TONE[state]}>{state}</Badge>
        </span>
      }
      note={`${p.variants.length} variants${options ? `: ${options}` : ""}`}
      actions={
        <>
          <RevisionChips ix={ix} revisions={p.revisions} value={bom.id} onChange={setRevId} />
          <Link to={bomLink(bom.id)} className="flex items-center gap-1 text-[12px] text-accent-text hover:underline">
            <Pencil className="size-3" /> Edit BOM
          </Link>
        </>
      }
    >
      {view === "grid" ? (
        <VariantGrid bom={bom} variants={p.variants} ix={ix} short={short} />
      ) : (
        <Tree bom={bom} variants={p.variants} ix={ix} short={short} vendorName={vendorName} packOf={packOf} />
      )}
    </Card>
  );
}

function SubBuild({
  bom,
  ix,
  short,
  vendorName,
  packOf,
  unlinked = false,
}: {
  bom: BomRow;
  ix: BomIndex;
  short: (s: string) => string;
  vendorName: (id: string) => string;
  packOf: PackOf;
  /** A product BOM that names the project while its product is linked to none: its variants, and "product not linked". */
  unlinked?: boolean;
}) {
  const item = ix.itemById.get(bom.item_id ?? "");
  const state = revisionState(ix, bom);
  const variants = bom.shopify_product_id ? (ix.variantsByProduct.get(bom.shopify_product_id) ?? []) : item ? [item] : [];
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {item ? <span className="font-mono text-[12px]">{item.sku}</span> : null}
          {ownerLabel(ix, bom)}
          <span className="font-normal text-muted">Rev {bom.revision}</span>
          <Badge tone={STATE_TONE[state]}>{state}</Badge>
        </span>
      }
      note={unlinked ? "product not linked: its BOM names this project (link it on Products)" : undefined}
      actions={
        <Link to={bomLink(bom.id)} className="flex items-center gap-1 text-[12px] text-accent-text hover:underline">
          <Pencil className="size-3" /> Edit BOM
        </Link>
      }
    >
      <Tree bom={bom} variants={variants} ix={ix} short={short} vendorName={vendorName} packOf={packOf} />
    </Card>
  );
}

// The module tree ---------------------------------------------------------------------------------------

function Tree({
  bom,
  variants,
  ix,
  short,
  vendorName,
  packOf,
}: {
  bom: BomRow;
  variants: BomItem[];
  ix: BomIndex;
  short: (s: string) => string;
  vendorName: (id: string) => string;
  packOf: PackOf;
}) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const sections = useMemo(() => sectionsOf(bom, ix.linesByBom.get(bom.id) ?? [], (l) => isPackagingRow(ix, l)), [bom, ix]);
  const toggle = (key: string) =>
    setOpen((o) => {
      const next = new Set(o);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  if (!sections.length) return <div className="px-4 py-3 text-muted">No lines yet.</div>;
  return (
    <div>
      {sections.map((s) => (
        <div key={s.name}>
          <div className="flex items-center gap-2 border-t border-line bg-surface-2/70 px-4 py-1 text-[11px] font-medium tracking-wide text-muted uppercase first:border-t-0">
            {sectionLabel(s, sections)}
            <span className="font-normal text-faint normal-case">{s.lines.length}</span>
          </div>
          {s.lines.map((line) => (
            <LineRows
              key={line.id}
              line={line}
              pathKey={line.id}
              depth={0}
              variants={variants}
              ix={ix}
              short={short}
              open={open}
              toggle={toggle}
              seen={[bom.item_id ?? ""]}
              vendorName={vendorName}
              packOf={packOf}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

const CHIP = { when: "bg-accent-soft text-accent-text", option: "bg-surface-2 text-text", override: "bg-warn-soft text-warn" } as const;

function LineRows({
  line,
  pathKey,
  depth,
  variants,
  ix,
  short,
  open,
  toggle,
  seen,
  vendorName,
  packOf,
}: {
  vendorName: (id: string) => string;
  packOf: PackOf;
  line: BomLineRow;
  pathKey: string;
  depth: number;
  variants: BomItem[];
  ix: BomIndex;
  short: (s: string) => string;
  open: Set<string>;
  toggle: (key: string) => void;
  seen: string[];
}) {
  const item = line.component_item_id ? ix.itemById.get(line.component_item_id) : undefined;
  const sub = item && item.source === "local" && !seen.includes(item.id) ? bomOf(ix, item) : undefined;
  const subLines = sub ? (ix.linesByBom.get(sub.id) ?? []) : [];
  const picks = optionPicks(ix, line, variants);
  const rules = lineRules(ix, line, variants);
  const pack = packOf(line);
  const expandable = subLines.length > 0;
  const isOpen = open.has(pathKey);
  const name = item
    ? item.name
    : line.option_name
      ? `${line.ref_designator ? `${line.ref_designator.charAt(0)}${line.ref_designator.slice(1).toLowerCase()}` : "Part"} by ${line.option_name}`
      : "No part";
  const openItem = (id: string | null | undefined) => id && setSearch({ open: id });
  return (
    <>
      <div className="flex items-start gap-2 border-t border-line px-4 py-1.5 hover:bg-hover/60" style={{ paddingLeft: 16 + depth * 22 }}>
        {expandable ? (
          <button
            type="button"
            onClick={() => toggle(pathKey)}
            className="mt-0.5 size-4 shrink-0 rounded text-faint hover:bg-hover hover:text-text"
            aria-label={isOpen ? "Collapse" : "Expand"}
            aria-expanded={isOpen}
          >
            {isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
        ) : (
          <span className="size-4 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            {item ? (
              <button type="button" onClick={() => openItem(item.id)} className="w-28 shrink-0 truncate text-left font-mono text-[12px] hover:underline">
                {short(item.sku)}
              </button>
            ) : (
              <span className={cx("w-28 shrink-0 truncate font-mono text-[12px]", line.option_name ? "text-muted" : "text-danger")}>
                {line.option_name ? "—" : "missing"}
              </span>
            )}
            <span className="min-w-0 truncate">{name}</span>
            {sub && (
              <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted">
                <Package className="size-3" />
                {builtInside(sub, ix.bomById.get(line.bom_id)?.make_vendor_id ?? null, line.supply)
                  ? "built in the same order"
                  : `built by ${sub.make_vendor_id ? vendorName(sub.make_vendor_id) : "?"} on its own`}{" "}
                · {subLines.length} parts · Rev {sub.revision} {revisionState(ix, sub)}
              </span>
            )}
          </div>
          {picks.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {picks.map((pk) => {
                const part = pk.itemId ? ix.itemById.get(pk.itemId) : undefined;
                return (
                  <button
                    key={pk.itemId ?? "none"}
                    type="button"
                    onClick={() => openItem(pk.itemId)}
                    title={part?.name}
                    className={cx(
                      "inline-flex h-5 items-center gap-1 rounded border px-1.5 text-[11px]",
                      part ? "border-line hover:border-line-strong" : "border-danger bg-danger-soft text-danger",
                    )}
                  >
                    <span className="text-muted">{pk.values.join(", ")}</span>
                    <span className="font-mono">{part ? short(part.sku) : "no part"}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
          {rules.map((c) => (
            <span
              key={c.text}
              title={c.title}
              className={cx("inline-flex h-5 items-center rounded px-1.5 text-[11px] font-medium whitespace-nowrap", CHIP[c.kind])}
            >
              {c.text}
            </span>
          ))}
          {byCm(line.supply) && <Badge>{SUPPLY_LABELS[line.supply]}</Badge>}
          {pack && (
            <span title={pack.title}>
              <Badge tone={pack.warn ? "warn" : "neutral"}>
                <Package className="mr-1 size-3" />
                {pack.text}
              </Badge>
            </span>
          )}
          <span className="w-12 text-right font-mono text-[12px] text-muted">×{qty(line.qty)}</span>
        </div>
      </div>
      {isOpen &&
        sub &&
        subLines.map((l) => (
          <LineRows
            key={l.id}
            line={l}
            pathKey={`${pathKey}/${l.id}`}
            depth={depth + 1}
            variants={variants}
            ix={ix}
            short={short}
            open={open}
            toggle={toggle}
            seen={[...seen, item?.id ?? ""]}
            vendorName={vendorName}
            packOf={packOf}
          />
        ))}
    </>
  );
}

// The variant grid ---------------------------------------------------------------------------------------

function VariantGrid({ bom, variants, ix, short }: { bom: BomRow; variants: BomItem[]; ix: BomIndex; short: (s: string) => string }) {
  const { groups, options } = useMemo(() => variantColumns(variants), [variants]);
  const sections = useMemo(() => sectionsOf(bom, ix.linesByBom.get(bom.id) ?? [], (l) => isPackagingRow(ix, l)), [bom, ix]);
  const cols = groups.flatMap((g) => g.variants);
  const groupOption = groups[0]?.name ?? "";
  const width = 92;
  const row = (line: BomLineRow): ReactNode => {
    const cells = cols.map((v) => lineCell(ix, line, v.id, variantOptions(v)));
    const used = cells.filter((c) => c.qty > 0 && c.componentId);
    const same = used.length > 0 && used.every((c) => c.componentId === used[0].componentId && c.qty === used[0].qty);
    const item = line.component_item_id ? ix.itemById.get(line.component_item_id) : undefined;
    const label = item ? short(item.sku) : `${line.ref_designator || "Part"} by ${line.option_name}`;
    return (
      <tr key={line.id} className="border-t border-line hover:bg-hover/60">
        <td className="sticky left-0 z-10 bg-surface px-3 py-1" title={item?.name}>
          <span className="block w-44 truncate font-mono text-[12px]">{label}</span>
        </td>
        {cells.map((c, i) => {
          const v = cols[i];
          const part = c.componentId ? ix.itemById.get(c.componentId) : undefined;
          const missing = c.qty > 0 && !c.componentId;
          const first = groups.some((g) => g.variants[0] === v);
          return (
            <td
              key={v.id}
              title={`${v.shopify_variant_title || v.sku}: ${missing ? "no part" : c.qty > 0 ? `${part?.sku ?? ""} ×${qty(c.qty)}` : "not in this variant"}`}
              onClick={() => part && setSearch({ open: part.id })}
              className={cx(
                "px-1.5 py-1 text-center font-mono text-[11px]",
                first && "border-l border-line-strong",
                missing && "bg-danger-soft text-danger",
                part && "cursor-pointer",
                same && "text-faint",
              )}
              style={{ minWidth: width, maxWidth: width }}
            >
              {missing ? (
                "missing"
              ) : c.qty > 0 && part ? (
                <span className="block truncate">
                  {short(part.sku)}
                  {c.qty !== 1 && <span className="text-faint"> ×{qty(c.qty)}</span>}
                  {c.overridden && <span className="text-warn"> •</span>}
                </span>
              ) : (
                <span className="text-line-strong">·</span>
              )}
            </td>
          );
        })}
      </tr>
    );
  };
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-[12px]">
        <thead>
          {options.length > 1 && (
            <tr>
              <th className="sticky left-0 z-10 bg-surface px-3 pt-2 text-left text-[11px] font-normal text-faint">{groupOption}</th>
              {groups.map((g) => (
                <th key={g.value} colSpan={g.variants.length} className="border-l border-line-strong px-2 pt-2 text-left text-[12px] font-semibold">
                  {g.value} <span className="font-normal text-faint">{g.variants.length}</span>
                </th>
              ))}
            </tr>
          )}
          <tr>
            <th className="sticky left-0 z-10 bg-surface px-3 pb-1.5 text-left text-[11px] font-normal text-faint">
              {options
                .slice(1)
                .map((o) => o.name)
                .join(" / ") || "Variant"}
            </th>
            {cols.map((v) => (
              <th
                key={v.id}
                className={cx(
                  "px-1.5 pb-1.5 align-bottom text-[11px] leading-tight font-normal text-muted",
                  groups.some((g) => g.variants[0] === v) && "border-l border-line-strong",
                )}
                style={{ minWidth: width, maxWidth: width }}
                title={v.sku}
              >
                {options.length > 1 ? columnLabel(v, groupOption) : v.shopify_variant_title || v.sku}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((s) => [
            <tr key={`s:${s.name}`} className="border-t border-line bg-surface-2/70">
              <td colSpan={cols.length + 1} className="sticky left-0 px-3 py-1 text-[11px] font-medium tracking-wide text-muted uppercase">
                {sectionLabel(s, sections)}
              </td>
            </tr>,
            ...s.lines.map(row),
          ])}
        </tbody>
      </table>
      <div className="flex gap-4 border-t border-line px-4 py-2 text-[11px] text-faint">
        <span>Grey: the same part in every variant that has the line</span>
        <span>· not in that variant</span>
        <span className="text-danger">missing: the variant needs a part here</span>
        <span className="text-warn">• changed for that variant</span>
      </div>
    </div>
  );
}
